import { ControllerRenderProps, FieldValues, Path, useFormContext } from 'react-hook-form'
import { IClassNameProps } from '@/utils/typings/class-name-props'
import { ComponentProps } from 'react'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { InputProps } from '@/components/ui/input'

interface GenericFormFieldProps<T extends FieldValues, TName extends Path<T> = Path<T>> extends Omit<
    InputProps,
    'name'
> {
    label?: string
    name: TName
    render: ({ field }: { field: ControllerRenderProps<T, TName> }) => React.ReactNode
    itemClassName?: IClassNameProps['className']
    formFieldProps?: Partial<Omit<ComponentProps<typeof FormField<T, TName>>, 'name' | 'control' | 'render'>>
    withoutControl?: boolean
    labelClassName?: IClassNameProps['className']
    hideFormMessage?: boolean
}

export const GenericFormField = <T extends FieldValues, TName extends Path<T> = Path<T>>({
    name,
    label,
    render,
    itemClassName,
    formFieldProps,
    withoutControl = false,
    labelClassName,
    hideFormMessage = false,
}: GenericFormFieldProps<T, TName>) => {
    const { control, clearErrors } = useFormContext<T>()

    return (
        <FormField<T, TName>
            control={control}
            name={name}
            render={({ field }) => {
                // Enhance field handlers with automatic error clearing
                const enhancedField = {
                    ...field,
                    onChange: (e: any) => {
                        field.onChange(e)
                        clearErrors(name)
                    },
                    onFocus: () => clearErrors(name),
                    onBlur: field.onBlur,
                }

                return (
                    <FormItem className={itemClassName}>
                        {label && <FormLabel className={labelClassName}>{label}</FormLabel>}
                        <FormControl withoutStyles={withoutControl}>{render({ field: enhancedField })}</FormControl>
                        {!hideFormMessage && <FormMessage />}
                    </FormItem>
                )
            }}
            {...formFieldProps}
        />
    )
}
